import type { History, To } from 'history';
import { action, computed, makeObservable, observable } from 'mobx';
import { v4 as uuid } from 'uuid';

import { ChangeJobOwnerInput, MutationUpdateJobSourceArgs, ReviseReportMutationVariables, StartJobInput, UpdateJobInput } from '@clipr/lib';

import { ApiVariables, ApiQueryOptions } from '../api/apiSchema';
import { ApiService } from '../api/apiService';
import Routes, { type LocationDescriptor } from '../routes';
import { MasterSidebarState } from '../components/layout/masterSidebarState';
import { assertNotNull, AsyncResult } from '../core';
import { Maybe } from '../core/types';
import { Bookmark, BookmarkProps } from '../entities/bookmark';
import { BookmarkList, BookmarkListProps } from '../entities/bookmarkList';
import { BookmarkManager } from '../entities/bookmarkManager';
import { JobModel, JobProps } from '../entities/job';
import { JobManager } from '../entities/jobManager';
import { MomentModel } from '../entities/moment';
import { MOMENT_SELECTOR_STATE_KEY } from '../entities/moments/momentSelector';
import { SpeakerModel, SpeakerProps } from '../entities/speaker';
import { SpeakerManager } from '../entities/speakerManager';
import { TeamManager } from '../entities/teamManager';
import { UserManager } from '../entities/userManager';
import { UserProfile } from '../entities/userProfile';
import { AnalyticsService } from '../services/analytics';
import { AuthService } from '../services/auth/authService';
import { LibraryService } from '../services/libraries/libraryService';
import { NetworkService } from '../services/network';
import { NotificationService } from '../services/notifications';
import { OverlayService } from '../services/overlays';
import { ProxyService } from '../services/proxy';
import { RoutingService } from '../services/routing';
import { StorageService } from '../services/storage';
import { UIService } from '../services/ui';
import { UploadService } from '../services/upload/uploadService';
import { WidgetService } from '../services/widget';
import { StoreNodeProxy } from './storeNodeProxy';
import { createMessage, Message, MessageHandlerWaitMode, StoreNode } from './tree';
import { Kernel } from '../kernel/kernel';
import { INIT_DEBUGGER, TRACE } from '../core/debug/debugMacros';
import { DiagnosticsService } from '../services/diagnostics/diagnosticsService';
import { PolicyService } from '../services/policy';
import { AiService } from '../services/ai/aiService';
import { SearchService } from '../services/search/searchService';

import { createPageStores, PageStores } from './page-stores.factory';
import { createWindowStores, WindowStores } from './window-stores.factory';
import { createWidgetStores, WidgetStores } from './widget-stores.factory';
import { nodeDispatch as dispatchNode } from './node-dispatch';

export class Store {

  readonly nodeType: 'Store' = 'Store';
  readonly id = uuid();

  constructor() {
    makeObservable<this, 'nodes'>(this, {
      isInitialized: observable,
      user: computed,
      isAuthorized: computed,
      isCloseTabWarningEnabled: observable,
      nodes: computed,
      jobs: computed,
      adminJobs: computed,
      bookmarks: computed,
      bookmarkLists: computed,
      moments: computed,
      init: action,
      insertJob: action,
      insertBookmark: action,
      removeBookmark: action,
      insertBookmarkList: action,
      removeBookmarkList: action,
      insertSpeaker: action,
      register: action,
      unregister: action,
      nodeBroadcast: action,
      nodeEmit: action,
      nodeDispatch: action,
      dispatch: action,
    });

    INIT_DEBUGGER(this, { color: 'red' });
    TRACE(this, `constructor()`);

    Object.assign(this, createPageStores(this));
    Object.assign(this, createWindowStores(this));
    Object.assign(this, createWidgetStores(this));
  }

  readonly kernel = new Kernel(this);

  get history(): History {
    return this.routing.history;
  }

  isInitialized = true;

  get user(): UserProfile | null {
    return this.auth.userProfile;
  }
  get isAuthorized() {
    return this.auth.isAuthorized;
  }

  isCloseTabWarningEnabled: boolean = false;

  protected readonly nodeLookup = observable.map<string, StoreNodeProxy>();
  protected get nodes() {
    return [...this.nodeLookup.values()];
  }

  // #region Entities
  get jobs(): JobModel[] {
    return this.jobManager.jobs;
  }

  get adminJobs(): JobModel[] {
    return this.jobManager.adminJobs;
  }

  get bookmarks(): Bookmark[] {
    return this.bookmarkManager.bookmarks;
  }

  get bookmarkLists(): BookmarkList[] {
    return this.bookmarkManager.bookmarkLists;
  }

  get moments(): MomentModel[] {
    return this.jobManager.moments;
  }
  // #endregion

  // #region Services
  readonly apiService = new ApiService(this);
  get api() { return this.apiService; }

  readonly authService = new AuthService(this);
  get auth() { return this.authService; }

  readonly notificationService = new NotificationService(this);
  get notifications() { return this.notificationService; }

  readonly aiService = new AiService(this);
  get ai() { return this.aiService; }

  readonly searchService = new SearchService(this);
  get search() { return this.searchService; }

  readonly uploadService = new UploadService(this);
  get upload() { return this.uploadService; }

  readonly overlayService = new OverlayService(this);
  get overlays() { return this.overlayService; }

  readonly routingService = new RoutingService(this);
  get router() { return this.routingService; }
  get routing() { return this.routingService; }

  readonly analyticsService = new AnalyticsService(this);
  get analytics() { return this.analyticsService; }

  readonly networkService = new NetworkService(this);
  get network() { return this.networkService; }

  readonly storageService = new StorageService(this);
  get storage() { return this.storageService; }

  readonly widgetService = new WidgetService(this);
  get widget() { return this.widgetService; }

  readonly libraryService = new LibraryService(this);
  get googleDrive() { return this.libraryService.googleDriveService; }
  get oneDrive() { return this.libraryService.oneDriveService; }
  get zoom() { return this.libraryService.zoomService; }

  readonly uiService = new UIService(this);
  get ui() { return this.uiService; }

  readonly proxyService = new ProxyService(this);
  get proxy() { return this.proxyService; }

  readonly diagnosticsService = new DiagnosticsService(this);
  get diagnostics() { return this.diagnosticsService; }

  readonly policyService = new PolicyService(this);
  get policy() { return this.policyService; }

  get resourceService() { return this.kernel.resourceService; }
  get resource() { return this.resourceService; }

  get vendorService() { return this.kernel.vendorService; }
  get vendor() { return this.vendorService; }

  // #endregion

  // #region Service component shortcuts
  get fullscreen() {
    return this.uiService.fullscreen;
  }
  get orientation() {
    return this.uiService.orientation;
  }
  get autoplay() {
    return this.uiService.autoplay;
  }
  // #endregion


  // #region Managers
  readonly bookmarkManager = new BookmarkManager(this);
  readonly jobManager = new JobManager(this);
  readonly teamManager = new TeamManager(this);
  readonly userManager = new UserManager(this);
  readonly speakerManager = new SpeakerManager(this);
  // #endregion

  // #region Components
  readonly pageSidebar = new MasterSidebarState(this);
  // #endregion

  // #region Pages
  readonly defaultPage!: PageStores['defaultPage'];
  readonly loginPage!: PageStores['loginPage'];
  readonly registerPage!: PageStores['registerPage'];
  readonly onboardPage!: PageStores['onboardPage'];
  readonly forgotPasswordPage!: PageStores['forgotPasswordPage'];
  readonly oauthCallbackPage!: PageStores['oauthCallbackPage'];
  readonly oauthLogoutPage!: PageStores['oauthLogoutPage'];
  readonly unsubscribePage!: PageStores['unsubscribePage'];
  readonly userSplashPage!: PageStores['userSplashPage'];
  readonly userPlayerPage!: PageStores['userPlayerPage'];
  readonly userDashboardPage!: PageStores['userDashboardPage'];
  readonly trainerVideoPage!: PageStores['trainerVideoPage'];
  readonly trainerDashboardPage!: PageStores['trainerDashboardPage'];
  readonly teamDashboardPage!: PageStores['teamDashboardPage'];
  readonly teamSettingsPage!: PageStores['teamSettingsPage'];
  readonly liveStreamQueuePage!: PageStores['liveStreamQueuePage'];
  readonly adminTeamsPage!: PageStores['adminTeamsPage'];
  readonly uploadPage!: PageStores['uploadPage'];
  readonly documentUploadPage!: PageStores['documentUploadPage'];
  readonly documentPage!: PageStores['documentPage'];
  readonly trainerDocumentPage!: PageStores['trainerDocumentPage'];
  readonly bookmarksPage!: PageStores['bookmarksPage'];
  readonly searchResultsPage!: PageStores['searchResultsPage'];
  readonly cliprGptSearchResultPageState!: PageStores['cliprGptSearchResultPageState'];
  readonly createTeamPage!: PageStores['createTeamPage'];
  readonly profilePage!: PageStores['profilePage'];
  readonly analyticsPage!: PageStores['analyticsPage'];
  readonly trainPage!: PageStores['trainPage'];
  readonly agencySignupPage!: PageStores['agencySignupPage'];
  readonly agencyDashboardPage!: PageStores['agencyDashboardPage'];
  readonly adminAgenciesPage!: PageStores['adminAgenciesPage'];
  readonly adminAgencyPage!: PageStores['adminAgencyPage'];
  // #endregion

  // #region Windows
  readonly editorWindow!: WindowStores['editorWindow'];
  readonly bookmarkWindow!: WindowStores['bookmarkWindow'];
  readonly bookmarkListWindow!: WindowStores['bookmarkListWindow'];
  readonly deleteBookmarkListWindow!: WindowStores['deleteBookmarkListWindow'];
  readonly shareVideoWindow!: WindowStores['shareVideoWindow'];
  readonly shareMeetingAssetsWindow!: WindowStores['shareMeetingAssetsWindow'];
  readonly multipleClipEditWindow!: WindowStores['multipleClipEditWindow'];
  readonly teamMembersWindow!: WindowStores['teamMembersWindow'];
  readonly teamDictionaryWindow!: WindowStores['teamDictionaryWindow'];
  readonly copyJobWindow!: WindowStores['copyJobWindow'];
  readonly addJobToTeamWindow!: WindowStores['addJobToTeamWindow'];
  readonly removeJobFromTeamWindow!: WindowStores['removeJobFromTeamWindow'];
  readonly deleteJobWindow!: WindowStores['deleteJobWindow'];
  readonly retryJobWindow!: WindowStores['retryJobWindow'];
  readonly downloadJobWindow!: WindowStores['downloadJobWindow'];
  readonly exportToEdlWindow!: WindowStores['exportToEdlWindow'];
  readonly deleteStreamWindow!: WindowStores['deleteStreamWindow'];
  readonly removeJobFromCliprWindow!: WindowStores['removeJobFromCliprWindow'];
  readonly ingestFromExternalLibraryWindow!: WindowStores['ingestFromExternalLibraryWindow'];
  readonly renameJobWindow!: WindowStores['renameJobWindow'];
  readonly changeJobOwnerWindow!: WindowStores['changeJobOwnerWindow'];
  readonly playerTutorialHelpButtonTooltip!: WindowStores['playerTutorialHelpButtonTooltip'];
  readonly playerTutorialWindow!: WindowStores['playerTutorialWindow'];
  readonly createFirstLibraryGuidedTraining!: WindowStores['createFirstLibraryGuidedTraining'];
  readonly accountGuidedTraining!: WindowStores['accountGuidedTraining'];
  readonly adminGuidedTraining!: WindowStores['adminGuidedTraining'];
  readonly uploadGuidedTraining!: WindowStores['uploadGuidedTraining'];
  readonly documentUploadGuidedTraining!: WindowStores['documentUploadGuidedTraining'];
  readonly postRegistrationWindow!: WindowStores['postRegistrationWindow'];
  readonly playerTutorialTopicsWindow!: WindowStores['playerTutorialTopicsWindow'];
  readonly playerTutorialConfirmationWindowState!: WindowStores['playerTutorialConfirmationWindowState'];
  readonly speakerWindow!: WindowStores['speakerWindow'];
  readonly trackWindow!: WindowStores['trackWindow'];
  readonly deleteTrack!: WindowStores['deleteTrack'];
  readonly removeAccessPopup!: WindowStores['removeAccessPopup'];
  readonly leaveTeamWindow!: WindowStores['leaveTeamWindow'];
  readonly videoInformationWindow!: WindowStores['videoInformationWindow'];
  readonly externalLibraryUploadWindow!: WindowStores['externalLibraryUploadWindow'];
  readonly makeTeamPublicWindow!: WindowStores['makeTeamPublicWindow'];
  readonly toggleCliprGptWindow!: WindowStores['toggleCliprGptWindow'];
  readonly togglePublicSafetyWindow!: WindowStores['togglePublicSafetyWindow'];
  readonly toggleDeceptioAiWindow!: WindowStores['toggleDeceptioAiWindow'];
  readonly cancelUploadWindow!: WindowStores['cancelUploadWindow'];
  readonly deleteMomentsPopup!: WindowStores['deleteMomentsPopup'];
  readonly clipWindow!: WindowStores['clipWindow'];
  readonly bulkInvitesWindow!: WindowStores['bulkInvitesWindow'];
  readonly closeWindowConfirmationModal!: WindowStores['closeWindowConfirmationModal'];
  readonly confirmationModal!: WindowStores['confirmationModal'];
  readonly mergeTracksWindow!: WindowStores['mergeTracksWindow'];
  readonly jobErrorsWindow!: WindowStores['jobErrorsWindow'];
  readonly commentDeleteWindow!: WindowStores['commentDeleteWindow'];
  readonly jobViewStatusWindow!: WindowStores['jobViewStatusWindow'];
  readonly credentialsExpiredWindow!: WindowStores['credentialsExpiredWindow'];
  readonly zoomDateSelectorWindow!: WindowStores['zoomDateSelectorWindow'];
  readonly uploadThumbnailWindow!: WindowStores['uploadThumbnailWindow'];
  readonly videoDetailsWindow!: WindowStores['videoDetailsWindow'];
  readonly scheduleStreamWindow!: WindowStores['scheduleStreamWindow'];
  readonly assetsWindow!: WindowStores['assetsWindow'];
  readonly liveFeedWindow!: WindowStores['liveFeedWindow'];
  readonly liveNotStreamingWindow!: WindowStores['liveNotStreamingWindow'];
  readonly manualIngestWindow!: WindowStores['manualIngestWindow'];
  readonly streamQueueSettingsWindow!: WindowStores['streamQueueSettingsWindow'];
  readonly trainerKeyboardShortcutsWindow!: WindowStores['trainerKeyboardShortcutsWindow'];
  readonly infoModal!: WindowStores['infoModal'];
  readonly downloadWindow!: WindowStores['downloadWindow'];
  readonly downloadJobSlidesWindow!: WindowStores['downloadJobSlidesWindow'];
  readonly teamProductWindow!: WindowStores['teamProductWindow'];
  readonly deleteTeamWindow!: WindowStores['deleteTeamWindow'];
  readonly speakerIdWindow!: WindowStores['speakerIdWindow'];
  readonly cancelAccountWindow!: WindowStores['cancelAccountWindow'];
  readonly deceptioWindow!: WindowStores['deceptioWindow'];
  // #endregion

  // #region Widgets
  readonly playerWidget!: WidgetStores['playerWidget'];
  readonly playerWidgetInjector!: WidgetStores['playerWidgetInjector'];
  readonly uploadWidget!: WidgetStores['uploadWidget'];
  readonly uploadCompactWidget!: WidgetStores['uploadCompactWidget'];
  readonly uploadWidgetInjector!: WidgetStores['uploadWidgetInjector'];
  readonly teamLibraryWidget!: WidgetStores['teamLibraryWidget'];
  readonly teamWidgetInjector!: WidgetStores['teamWidgetInjector'];
  readonly liveStreamQueueWidget!: WidgetStores['liveStreamQueueWidget'];
  readonly liveStreamQueueWidgetInjector!: WidgetStores['liveStreamQueueWidgetInjector'];
  readonly analyticsWidget!: WidgetStores['analyticsWidget'];
  readonly analyticsWidgetInjector!: WidgetStores['analyticsWidgetInjector'];
  // #endregion

  /**
   * Runs the initialization tasks for the Store, including fetching required data.
   */
  init() {

    const {
      diagnosticsService,

      routingService,
      proxyService,
      authService,
      analyticsService,
      widgetService
    } = this;

    // configure services
    authService.registerProfileStorageKey(MOMENT_SELECTOR_STATE_KEY);

    // initialize Services
    if (import.meta.env.PROD)
      diagnosticsService.init();

    routingService.init();
    proxyService.init();
    authService.init();
    analyticsService.init();
    widgetService.init();

    this.isInitialized = true;
  }

  dispose() {
    this.auth.dispose();
  }

  // #region Routing
  // -------

  /** Shortcut for `RoutingService.goTo`. */
  goTo(route: string | To | LocationDescriptor) {
    this.routing.goTo(route);
  }

  // #endregion

  // #region Jobs
  // -------
  hasJob(id: string) {
    return this.jobManager.hasJob(id);
  }

  getJob(id: string) {
    return this.jobManager.getJob(id);
  }
  maybeGetJob(id: Maybe<string>) {
    return this.jobManager.maybeGetJob(id);
  }

  insertJob(jobData: JobProps) {
    return this.jobManager.insertJob(jobData);
  }

  async apiFetchJob(id: string, deps = false, opts?: ApiQueryOptions) {
    return this.jobManager.apiFetchJob(id, deps, opts);
  }

  async apiStartJob(params: StartJobInput): AsyncResult<JobModel> {
    return this.jobManager.apiStartJob(params);
  }

  async apiUpdateJob(params: UpdateJobInput): AsyncResult<JobModel> {
    return this.jobManager.apiUpdateJob(params);
  }

  async apiReviseReport(input: ReviseReportMutationVariables['input']): AsyncResult<JobModel> {
    return this.jobManager.apiReviseReport(input);
  }

  async apiUpdateJobSource(params: MutationUpdateJobSourceArgs): AsyncResult<JobModel> {
    return this.jobManager.apiUpdateJobSource(params);
  }

  async apiChangeJobOwner(params: ChangeJobOwnerInput): AsyncResult<JobModel> {
    return this.jobManager.apiChangeJobOwner(params);
  }

  getMoment(id: string) {
    return this.jobManager.getMoment(id);
  }
  // #endregion

  // #region Bookmark
  // -------
  getBookmark(id: string) {
    return this.bookmarkManager.getBookmark(id);
  }
  maybeGetBookmark(id: string) {
    return this.bookmarkManager.maybeGetBookmark(id);
  }

  insertBookmark(props: BookmarkProps) {
    return this.bookmarkManager.insertBookmark(props);
  }

  removeBookmark(id: string) {
    return this.bookmarkManager.removeBookmark(id);
  }

  async apiFetchBookmarks(args: ApiVariables<'getBookmarks'>, opts?: ApiQueryOptions): AsyncResult<Bookmark[]> {
    return this.bookmarkManager.apiFetchBookmarks(args, opts);
  }

  async apiCreateBookmark(args: ApiVariables<'createBookmark'>): AsyncResult<Bookmark> {
    return this.bookmarkManager.apiCreateBookmark(args);
  }

  async apiDeleteBookmark(args: ApiVariables<'deleteBookmark'>): AsyncResult<Bookmark> {
    return this.bookmarkManager.apiDeleteBookmark(args);
  }

  async apiFetchMomentBookmarks(args: ApiVariables<'getMomentBookmarks'>): AsyncResult<Bookmark[]> {
    return this.bookmarkManager.apiFetchMomentBookmarks(args);
  }

  async apiFetchJobRelatedBookmarks(args: ApiVariables<'getJobRelatedBookmarks'>, opts?: ApiQueryOptions): AsyncResult<Bookmark[]> {
    return this.bookmarkManager.apiFetchJobRelatedBookmarks(args, opts);
  }

  // #endregion

  // #region BookmarkList
  // -------
  hasBookmarkList(id: string) {
    return this.bookmarkManager.hasBookmarkList(id);
  }

  getBookmarkList(id: string): BookmarkList {
    return this.bookmarkManager.getBookmarkList(id);
  }

  insertBookmarkList(props: BookmarkListProps): BookmarkList {
    return this.bookmarkManager.insertBookmarkList(props);
  }

  removeBookmarkList(id: string): BookmarkList {
    return this.bookmarkManager.removeBookmarkList(id);
  }

  async apiFetchBookmarkLists(opts?: ApiQueryOptions): AsyncResult<BookmarkList[]> {
    return this.bookmarkManager.apiFetchBookmarkLists(opts);
  }

  async apiFetchBookmarkList(id: string): AsyncResult<BookmarkList> {
    return this.bookmarkManager.apiFetchBookmarkList(id);
  }

  async apiCreateBookmarkList(args: ApiVariables<'createBookmarkList'>): AsyncResult<BookmarkList> {
    return this.bookmarkManager.apiCreateBookmarkList(args);
  }

  async apiUpdateBookmarkList(args: ApiVariables<'updateBookmarkList'>): AsyncResult<BookmarkList> {
    return this.bookmarkManager.apiUpdateBookmarkList(args);
  }

  async apiDeleteBookmarkList(args: ApiVariables<'deleteBookmarkList'>): AsyncResult<BookmarkList> {
    return this.bookmarkManager.apiDeleteBookmarkList(args);
  }
  // #endregion

  // #region Speakers
  // -------
  hasSpeaker(id: string) {
    return this.speakerManager.hasSpeaker(id);
  }

  getSpeaker(id: string) {
    return this.speakerManager.getSpeaker(id);
  }
  maybeGetSpeaker(id: Maybe<string>) {
    return this.speakerManager.maybeGetSpeaker(id);
  }

  insertSpeaker(speakerData: SpeakerProps) {
    return this.speakerManager.insertSpeaker(speakerData);
  }

  async apiFetchSpeakers(args: ApiVariables<'getSpeakers'>, opts?: ApiQueryOptions) {
    return this.speakerManager.apiFetchSpeakers(args, opts);
  }

  async apiFetchSpeaker(args: ApiVariables<'getSpeaker'>, opts?: ApiQueryOptions) {
    return this.speakerManager.apiFetchSpeaker(args, opts);
  }

  async apiAddSpeaker(args: ApiVariables<'addSpeaker'>): AsyncResult<SpeakerModel> {
    return this.speakerManager.apiAddSpeaker(args);
  }

  async apiUpdateSpeaker(args: ApiVariables<'updateSpeaker'>): AsyncResult<SpeakerModel> {
    return this.speakerManager.apiUpdateSpeaker(args);
  }
  // #endregion

  // #region Tree
  // -------
  register<TNode extends StoreNode = StoreNode>(proxy: StoreNodeProxy<TNode>) {
    const { node } = proxy;
    this.nodeLookup.set(node.id, proxy as unknown as StoreNodeProxy);
    return proxy;
  }

  unregister(node: StoreNode) {
    this.nodeLookup.delete(node.id);
  }

  nodeBroadcast(msg: Message) {
    const proxy = this.nodeLookup.get(msg.sender.id);
    assertNotNull(proxy, `Received a 'broadcast' request from node ${msg.sender.id} which is not registered into the Store.`);

    this.nodes.forEach(proxy => {
      if (typeof proxy.receiver === 'function' &&
        proxy.receiveFilter.includes(msg.fullType)) {
        proxy.receiver(msg);
      }
    });
  }

  async nodeEmit(msg: Message, waitMode: MessageHandlerWaitMode = MessageHandlerWaitMode.None): Promise<void> {

    const proxy = this.nodeLookup.get(msg.sender.id);
    assertNotNull(proxy, `Received an 'emit' request from node ${msg.sender.id} which is not registered into the Store.`);

    const listeners = proxy.listeners;

    switch (waitMode) {

      case MessageHandlerWaitMode.Serial:
        for (const listener of listeners)
          await listener(msg);
        return;

      case MessageHandlerWaitMode.Parallel:
        await Promise.all(
          listeners.map(listener => listener(msg)));
        return;

      case MessageHandlerWaitMode.First:
        await Promise.any(
          listeners.map(listener => listener(msg)));
        return;

      default:
      case MessageHandlerWaitMode.None:
        listeners.forEach(listener =>
          listener(msg));
        return;
    }
  }

  async nodeDispatch(msg: Message) {
    return dispatchNode(this, msg);
  }

  dispatch(target: string, type: string, payload: unknown) {
    const msg = createMessage(null, 'dispatch', type, payload);
    msg.target = target;
    this.nodeDispatch(msg);
    return this;
  }
  // #endregion

  logout() {
    this.routingService.goTo(Routes.logout());
  }

}
